import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import TooltipLayer from './Tooltip';
import { placeMenu } from './selectOptions';

/**
 * The ResUI Select's menu (PRD-1702): the time picker's, Context Menu options
 * in a listbox fixed to the window. It mounts open: placed once rendered, then
 * focus moves into the list with the active option centred, and a label cut
 * short gets its full text in the ResUI Tooltip. A click outside, a scroll of
 * the page or a resize closes it; scrolling the list doesn't.
 */
function SelectMenu({ fieldRef, listRef, listId, list, chosen, active, byKeyboard, ariaLabel, labelledBy, onKeyDown, onBlur, onChoose, onClose }) {
  const popRef = useRef(null);
  const [place, setPlace] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    const pop = popRef.current;
    const lst = listRef.current;
    if (!pop || !lst || !fieldRef.current) return;
    if (!place) {
      setPlace(placeMenu(fieldRef.current, pop.offsetWidth, pop.offsetHeight));
      return;
    }
    lst.focus({ preventScroll: true });
    const el = lst.children[active];
    if (el) lst.scrollTop = Math.max(0, el.offsetTop - lst.clientHeight / 2 + el.offsetHeight / 2);
    lst.querySelectorAll('.resui-select-opt-text').forEach((t) => {
      if (t.scrollWidth > t.clientWidth) t.parentNode.dataset.tip = t.textContent;
      else delete t.parentNode.dataset.tip;
    });
    // Once placed: the active option scrolls itself into view as it moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  useEffect(() => {
    const outside = (e) => {
      if (!popRef.current?.contains(e.target) && !fieldRef.current?.contains(e.target)) closeRef.current(false);
    };
    const moved = (e) => {
      if (e.target?.nodeType === 1 && popRef.current?.contains(e.target)) return;
      closeRef.current(popRef.current?.contains(document.activeElement));
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('scroll', moved, true);
    window.addEventListener('resize', moved);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('scroll', moved, true);
      window.removeEventListener('resize', moved);
    };
  }, [fieldRef]);

  // Tab and a click elsewhere leave the list; the menu closes behind them.
  const handleBlur = (e) => {
    const to = e.relatedTarget;
    if (to && (popRef.current?.contains(to) || to === fieldRef.current)) return;
    onBlur();
  };

  return createPortal(
    <div
      ref={popRef}
      className={`resui-select-pop${byKeyboard ? ' is-kbd' : ''}`}
      style={place ? { left: place.left, top: place.top, minWidth: place.minWidth } : { visibility: 'hidden', left: 0, top: 0 }}
    >
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        tabIndex={-1}
        className="resui-select-scroll"
        aria-label={labelledBy ? undefined : ariaLabel}
        aria-labelledby={labelledBy}
        aria-activedescendant={list.length ? `${listId}-o${active}` : undefined}
        onKeyDown={onKeyDown}
        onBlur={handleBlur}
      >
        {list.map((o, k) => (
          <div
            // Labels can repeat (two documents may share a name); the place can't.
            // eslint-disable-next-line react/no-array-index-key
            key={k}
            id={`${listId}-o${k}`}
            role="option"
            aria-selected={k === chosen}
            className={`resui-select-opt${k === active ? ' is-active' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChoose(k)}
          >
            <span className="resui-select-opt-text">{o.label}</span>
            <Icon name="check" size={16} className="resui-select-check" />
          </div>
        ))}
      </div>
      <TooltipLayer scopeRef={popRef} />
    </div>,
    document.body,
  );
}

export default SelectMenu;
